import type { Module } from '../../../../api/module';
import type { NodeWithoutDescription } from '../../../../api/table-of-content';
import type { Medialink } from '../../../../api/medialink';
import type { UserMaterial } from '../../../../api/user-material';
import type { SlideSet } from '../../../../api/slide-set';
import type { groupResultsByModule } from '../utils/groupResultsByModule';
import { useTranslation } from 'react-i18next';

import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import Badge from '@pelckmans/elementis/components/badge';
import List from '@pelckmans/elementis/components/list';
import ListGroup from '@pelckmans/elementis/components/list-group';
import Section from '@pelckmans/elementis/components/section';

import ModuleStatus from '../../../../components/ModuleStatus';
import { useMaterialLinkBuilder } from '../../../../hooks/useMaterialLinkBuilder';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import MedialinkItem from '../../components/MedialinkItem';
import SlideSetItem from '../../components/SlideSetItem';
import UserMaterialItem from '../../components/UserMaterialItem';
import MaterialPills from '../../toc/components/MaterialPills';
import { SharedMaterial } from '../context/QueryMaterialContext';
import useAnalytics from '../../../../hooks/useAnalytics';
import { getFullHierarchyDescriptionOfNode } from '../../../../utils/getNodeDescription';
import { buildGroupProps } from '../../../../utils/elementisUtils';

type ResultsForModuleProps = {
  resultsForModule: ReturnType<typeof groupResultsByModule>[number];
  module: Module;
  refetch: () => void;
};

type MaterialForNodeProps = {
  node?: NodeWithoutDescription;
  module: Module;
  resultsForNode: {
    medialinks: Medialink[];
    userMaterial: UserMaterial[];
    slideSets: SlideSet[];
    sharedMaterial: SharedMaterial[];
  };
  selectedModuleSlug: string;
  refetch: () => void;
};

export function getPillGroup(
  medialink: Medialink,
  isTeacherAlike: boolean,
): { 'aria-label': undefined; children: NonNullable<React.ReactNode>; role: undefined } {
  return buildGroupProps(
    <MaterialPills
      isAssignment={medialink.assignments.length > 0}
      isShared={medialink.shares.length > 0}
      isStudent={isTeacherAlike && medialink.group === 'student'}
      isTeacher={isTeacherAlike && medialink.group === 'teacher'}
      isAdaptive={medialink.assessmentQExercise?.isAdaptive}
      isOnTheFly={medialink.assessmentQExercise?.isOnTheFly}
    />,
  );
}

function MaterialForNode({ node, module, resultsForNode, refetch, selectedModuleSlug }: MaterialForNodeProps) {
  const { t } = useTranslation();
  const { buildMaterialTocLink } = useMaterialLinkBuilder();
  const analytics = useAnalytics();

  if (!node) return null;

  const captureEvent = () => {
    analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.NODE_ACCESSED,
      objectId: node.id,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_CATEGORICAL_MATERIAL,
    });

    analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.MODULE_ACCESSED,
      objectId: module.id,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_CATEGORICAL_MATERIAL,
    });
  };

  const hierarchy =
    resultsForNode.medialinks[0]?.hierarchy ||
    resultsForNode.userMaterial[0]?.hierarchy ||
    resultsForNode.slideSets[0]?.hierarchy ||
    resultsForNode.sharedMaterial[0]?.hierarchy;

  return (
    <ListGroup>
      <List>
        <List.Header
          title={getFullHierarchyDescriptionOfNode(hierarchy, t)}
          titleTag="h3"
          prominent
          linkProps={{
            children: t('PAGES.CATEGORICAL.BROWSE_MODE.SECTION.MATERIAL_LIST.LINK'),
            to: buildMaterialTocLink(node.id, selectedModuleSlug) || '',
            onClick: captureEvent,
          }}
        />
        {resultsForNode.medialinks.map(medialink => (
          <MedialinkItem
            key={medialink.id}
            node={node}
            module={module}
            medialink={medialink}
            subLocationId={ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_CATEGORICAL_MATERIAL}
            refetch={refetch}
            getPillGroup={getPillGroup}
          />
        ))}
        {resultsForNode.userMaterial.map(userMaterial => (
          <UserMaterialItem
            userMaterial={userMaterial}
            key={userMaterial.id}
            nodes={[node]}
            module={module}
            subLocationId={ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_CATEGORICAL_MATERIAL}
            refetch={refetch}
            isShared={userMaterial.shared}
          />
        ))}
        {resultsForNode.slideSets.map(slideSet => (
          <SlideSetItem
            node={node}
            module={module}
            slideSet={slideSet}
            key={`${slideSet.id}-${slideSet.versionId}`}
            subLocationId={ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_CATEGORICAL_MATERIAL}
            refetch={refetch}
            isShared={slideSet.shared}
          />
        ))}
        {resultsForNode.sharedMaterial.map(sharedMaterial =>
          sharedMaterial.materialType === 'slide-set' ? (
            <SlideSetItem
              node={node}
              module={module}
              slideSet={sharedMaterial}
              key={`${sharedMaterial.id}-${sharedMaterial.versionId}`}
              subLocationId={ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_CATEGORICAL_MATERIAL}
              refetch={refetch}
              isShared={sharedMaterial.shares.length > 0}
            />
          ) : (
            <UserMaterialItem
              userMaterial={sharedMaterial}
              key={sharedMaterial.id}
              nodes={[node]}
              module={module}
              subLocationId={ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_CATEGORICAL_MATERIAL}
              refetch={refetch}
              isShared={sharedMaterial.shares.length > 0}
            />
          ),
        )}
      </List>
    </ListGroup>
  );
}

export default function ResultsForModule({ resultsForModule, module, refetch }: ResultsForModuleProps) {
  return (
    <Section
      title={module.niceName}
      avatar={
        <Avatar>
          <AvatarImage name={module.niceName} src={module.styling?.logo} />
        </Avatar>
      }
      counter={{
        badge: <Badge>{resultsForModule.count}</Badge>,
        label: 'Resultaten',
      }}
      status={<ModuleStatus module={module} showUnlicensedStatus />}
    >
      <ListGroup>
        {Object.entries(resultsForModule.groupedByNode).map(([nodeId, resultsForNode]) => (
          <MaterialForNode
            module={module}
            node={resultsForModule.nodes?.find(n => n.id === nodeId)}
            resultsForNode={resultsForNode}
            key={nodeId}
            selectedModuleSlug={module.slug}
            refetch={refetch}
          />
        ))}
      </ListGroup>
    </Section>
  );
}
